import { fmtMoneda } from '@/lib/cd2bPricing'
import PaquetesCapacidad from '@/components/cd2b/PaquetesCapacidad'
import ComparadorEdiciones from '@/components/cd2b/ComparadorEdiciones'
import SelectorModulos from '@/components/cd2b/SelectorModulos'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { AREAS_BASE } from './paquete'

/**
 * Paso 2 — Paquete: edición, asientos, módulos, lo que verá el cliente y su cupo de productos/DTE.
 * El precio y la comisión no se repiten aquí: van en el resumen fijo, a la vista mientras se marca.
 */
export default function PasoPaquete({ c }) {
  const { form, setForm, catalogo, quote, alcance, seatsBase, menuPrevio, tieneDemo } = c

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-h3">Paquete y alcance</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>Edición</Label>
              <Select value={form.edicion} onValueChange={c.cambiarEdicion}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catalogo.ediciones.map((ed) => (
                    <SelectItem key={ed.edicion} value={ed.edicion}>{ed.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {alcance.edicionDef?.desc && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">{alcance.edicionDef.desc}</p>
              )}
            </div>
            <div>
              <Label htmlFor="cot-seats-completos">Asientos completos</Label>
              <Input id="cot-seats-completos" type="number" min="0" inputMode="numeric" value={form.seats.completo}
                onChange={(e) => setForm({ ...form, seats: { ...form.seats, completo: Number(e.target.value) } })} />
              {seatsBase.completo != null && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">La edición incluye {seatsBase.completo}.</p>
              )}
            </div>
            <div>
              <Label htmlFor="cot-seats-operativos">Asientos operativos</Label>
              <Input id="cot-seats-operativos" type="number" min="0" inputMode="numeric" value={form.seats.operativo}
                onChange={(e) => setForm({ ...form, seats: { ...form.seats, operativo: Number(e.target.value) } })} />
              {seatsBase.operativo != null && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">La edición incluye {seatsBase.operativo}.</p>
              )}
            </div>
            <div>
              <Label>Precio mensual</Label>
              <p className="text-heading-sm font-semibold text-neutral-900 dark:text-neutral-50 mt-1">
                {fmtMoneda(quote.totalMensual, form.moneda)}
              </p>
              <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                Calculado del paquete. Para negociar, usa el descuento del paso 3.
              </p>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <p className="text-body-sm font-medium text-neutral-700 dark:text-neutral-300">Módulos a implementar</p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  La edición marca los módulos que trae (sin costo extra). Desmarca los que el
                  cliente no usará (se le ocultan). Agrega módulos adicionales o verticales: suman
                  al precio.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm"
                  onClick={() => setForm((f) => ({ ...f, modulosSel: c.modulosDeEdicion(f.edicion) }))}>
                  Todos los de la edición
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={() => setForm((f) => ({ ...f, modulosSel: [] }))}>
                  Ninguno
                </Button>
              </div>
            </div>

            <SelectorModulos
              modulos={catalogo.modulos}
              addons={[
                ...(catalogo.addons?.soporte || []),
                // Canales de acceso (la app del teléfono): se marcan o no, como el soporte.
                ...(catalogo.addons?.acceso || []),
                ...(catalogo.addons?.negocio?.code ? [catalogo.addons.negocio] : []),
              ]}
              edicion={form.edicion}
              seleccion={form.modulosSel}
              dependencias={quote.dependencias || []}
              onToggleModulo={c.toggleModulo}
              addonActivo={(a) => c.cargoActivo(a.code)}
              onToggleAddon={c.toggleAddonCargo}
              dinero={(n) => fmtMoneda(n, form.moneda)}
              extrasBloqueados={(catalogo.ediciones || []).find((e) => e.edicion === form.edicion)?.permiteExtrasPago === false}
            />
          </div>

          {/* Vista previa: el mismo cálculo que aplica la autoridad al firmar la licencia. */}
          <div className="rounded-lg border border-line bg-surface-muted p-3 space-y-2">
            <p className="text-body-sm font-medium text-neutral-700 dark:text-neutral-300">Lo que verá el cliente en la app</p>
            <div className="flex flex-wrap gap-1.5">
              {alcance.activas.map((a) => (
                <Badge key={a} variant={AREAS_BASE.includes(a) ? 'secondary' : 'success'}>
                  {catalogo.areaMeta?.[a]?.label || a}
                </Badge>
              ))}
            </div>
            {alcance.apagadas.length > 0 && (
              <div>
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mb-1">No verá ({alcance.apagadas.length}):</p>
                <div className="flex flex-wrap gap-1.5">
                  {alcance.apagadas.map((a) => (
                    <Badge key={a} variant="outline" className="line-through text-neutral-400 dark:text-neutral-500">
                      {catalogo.areaMeta?.[a]?.label || a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {menuPrevio && (
              <div className="rounded-md border border-line bg-surface p-2">
                <p className="text-body-sm text-neutral-800 dark:text-neutral-200">
                  Verá <strong>{menuPrevio.total}</strong> {menuPrevio.total === 1 ? 'entrada' : 'entradas'} de menú
                </p>
                <p className="text-caption text-neutral-500 dark:text-neutral-400">
                  {menuPrevio.grupos.map((g) => `${g.grupo} (${g.items.length})`).join(' · ')}
                </p>
                {!form.giro && (
                  <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                    Elige el rubro (paso 1) para afinar la cuenta: el menú también se recorta por giro.
                  </p>
                )}
              </div>
            )}
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Núcleo y Punto de venta vienen con toda edición: no se pueden apagar.
            </p>
            {form.modulosSel.length === 0 && (
              <p className="text-caption text-warning-700 dark:text-warning-300">
                Sin módulos marcados no hay restricción: el cliente verá todo lo que trae la edición.
              </p>
            )}
            {alcance.porDependencia.length > 0 && (
              <p className="text-caption text-warning-700 dark:text-warning-300">
                Se activan por dependencia aunque no vendiste su módulo:{' '}
                {alcance.porDependencia.map((a) => catalogo.areaMeta?.[a]?.label || a).join(', ')}.
              </p>
            )}
            {tieneDemo && (
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Al guardar, este alcance se aplica a la empresa demo del dueño (debe volver a entrar
                a la app para verlo).
              </p>
            )}
          </div>

          <ComparadorEdiciones catalogo={catalogo} edicionActual={form.edicion}
            modulosSel={form.modulosSel} moneda={form.moneda} onSubir={c.cambiarEdicion} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-h3">Cupo de productos y DTE</CardTitle></CardHeader>
        <CardContent>
          <PaquetesCapacidad
            catalogo={catalogo}
            capacidad={form.capacidad}
            setCapacidad={(capacidad) => setForm((f) => ({ ...f, capacidad }))}
            limites={quote.limites}
            moneda={form.moneda}
          />
        </CardContent>
      </Card>
    </div>
  )
}
